"use client";
import React, {
  useMemo,
  useRef,
  useEffect,
  useCallback,
  useState,
} from "react";
import DOMPurify from "dompurify";
import md from "@/app/utils/markdown";
import styles from "../aiAnswerQuestion/index.module.scss";
import { ImagePreview } from "@douyinfe/semi-ui";
import { ImageViewer } from "antd-mobile";
import { isMobileDevice } from "@/app/utils/device";

interface Props {
  content: string;
}

/**
 * 历史 AI SSE 回复的静态渲染组件。
 * 使用 markdown-it + DOMPurify，不依赖 Streamdown，无流式/打字机动效。
 * 样式复用 aiAnswerQuestion/index.module.scss 以保证与实时 SSE 视觉一致。
 */
function AiAnswerHistory({ content }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");

  const renderedHtml = useMemo(() => {
    if (!content) return "";
    let processed = content.replace(/<br\/>/g, "\n");
    processed = processed.replace(
      /!\[([^\]]*)\]\(([^)\s]+)(?:\s+=(\d*)x(\d*))?\s*\)/g,
      (match, alt, url, width, height) => {
        if (!width && !height) return match;
        let tag = `<img src="${url}" alt="${alt}"`;
        if (width) tag += ` width="${width}"`;
        if (height) tag += ` height="${height}"`;
        tag += ` />`;
        return tag;
      },
    );
    try {
      return DOMPurify.sanitize(md.render(processed), {
        ADD_ATTR: ["target"],
      });
    } catch {
      return DOMPurify.sanitize(processed, { ADD_ATTR: ["target"] });
    }
  }, [content]);

  const handleImageClick = useCallback((e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG" && target.parentElement?.tagName !== "A") {
      const url = target.getAttribute("src");
      if (url && /^https?:\/\//i.test(url)) {
        e.preventDefault();
        e.stopPropagation();
        setPreviewSrc(url);
        setPreviewVisible(true);
      }
    }
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener(
        "click",
        handleImageClick as unknown as EventListener,
      );
    }
    return () => {
      if (container) {
        container.removeEventListener(
          "click",
          handleImageClick as unknown as EventListener,
        );
      }
    };
  }, [handleImageClick]);

  if (!content) return null;
  return (
    <>
      <div className={styles.aiAnswerContainer}>
        <div
          ref={containerRef}
          className={styles.aiAnswerContent}
          dangerouslySetInnerHTML={{ __html: renderedHtml }}
        />
      </div>
      {isMobileDevice() ? (
        <ImageViewer
          image={previewSrc}
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
        />
      ) : (
        <ImagePreview
          onClose={() => setPreviewVisible(false)}
          src={previewSrc}
          visible={previewVisible}
          onVisibleChange={setPreviewVisible}
        />
      )}
    </>
  );
}

export default React.memo(AiAnswerHistory);
